import { FC } from "react";
import classNames from "classnames";
import { MdCheck } from "react-icons/md";
import { useTranslation } from "react-i18next";
import Carrier from "@Components/Carrier/Carrier";
import { DownIcon } from "src/assets/Icons";
import type { GuestTabItem, GuestTabsRenderProps } from "../GuestTabs";
import GuestStatusBadge from "../components/GuestStatusBadge";

const GuestTabsMob: FC<GuestTabsRenderProps> = ({ items, value, onChange }) => {
  const { t } = useTranslation();
  const activeIndex = Math.max(
    items.findIndex((item) => item.value === value),
    0,
  );
  const activeItem = items[activeIndex];
  const completedCount = items.filter((item) => item.isComplete).length;
  const rooms = items.reduce<Record<number, { item: GuestTabItem; index: number }[]>>((acc, item, index) => {
    (acc[item.roomIndex] ??= []).push({ item, index });
    return acc;
  }, {});

  return (
    <Carrier
      label={
        <div className="flex flex-col">
          <span className="text-lg font-semibold">{t("Hotels.guests.guestDetails")}</span>
          <span className="text-font-subtle text-xs font-medium">
            {t("Hotels.guests.guestsCompleted", { count: completedCount, total: items.length })}
          </span>
        </div>
      }
      renderTrigger={({ open }) => (
        <div className="bg-background-default flex flex-col gap-3 rounded-md p-5 shadow-sm">
          <div className="flex items-center gap-2">
            <button type="button" onClick={open} className="flex min-w-0 flex-1 cursor-pointer flex-col items-center">
              <span className="text-font-subtle text-xs font-medium">
                {t("Hotels.guests.guestProgress", { current: activeIndex + 1, total: items.length })}
              </span>
              <span className="text-font-default flex max-w-full items-center gap-1.5 text-base font-semibold">
                <span className="truncate">{activeItem?.name}</span>
                {activeItem?.isLead && (
                  <span className="bg-background-primary text-font-inverted flex h-4 w-4 shrink-0 items-center justify-center rounded-full">
                    <MdCheck className="h-3 w-3" />
                  </span>
                )}
                <DownIcon className="text-font-subtle h-3 w-3 shrink-0" />
              </span>
            </button>
          </div>
        </div>
      )}
      renderSheet={({ close }) => (
        <div className="flex flex-col gap-5 overflow-y-auto px-4 pb-6 p-4">
          {Object.entries(rooms).map(([roomIndex, roomItems]) => (
            <div key={roomIndex} className="flex flex-col gap-2">
              <div className="text-font-subtle text-xs font-semibold uppercase tracking-[0.5px]">
                {t("Hotels.search.roomNumber", { number: Number(roomIndex) + 1 })}
              </div>
              {roomItems.map(({ item, index }) => {
                const isActive = item.value === value;
                return (
                  <button
                    key={item.value}
                    type="button"
                    onClick={() => {
                      onChange(item.value);
                      close();
                    }}
                    className={classNames(
                      "flex w-full cursor-pointer items-center gap-3 rounded-md border p-3 text-start transition-colors",
                      isActive ? "border-border-active bg-background-primary/5" : "border-border-subtle",
                    )}
                  >
                    <GuestStatusBadge item={item} index={index} isActive={isActive} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-font-default truncate text-base font-semibold">{item.name}</span>
                      <span className="text-font-subtle text-xs">{t(`Hotels.guests.guestType.${item.type}`)}</span>
                    </span>
                    {item.isLead && (
                      <div className="bg-background-primary text-font-inverted shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium">
                        {t("Hotels.guests.primaryGuest")}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    />
  );
};

export default GuestTabsMob;
